import type { FC } from 'react';
import type { HeadingProps } from './types';

import styled from 'styled-components';

import {
  sanitizeRichText,
  defaultWebValue,
  filterBackgroundColor,
  COLOR_GRAY_13,
  COLOR_YELLOW,
  generateAccessKey
} from 'shared/helpers/index';
import { useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Wrapper } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';

const HeadSubhead = styled.div`
  padding: 60px 0;

  @media screen and (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Subheadline = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  margin: 0 auto;
  text-align: left;
  font-weight: 400;
  margin-bottom: 32px;
  color: ${COLOR_GRAY_13};
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 22px;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      margin: 0 auto;
      text-align: center;
      font-weight: 400;
      margin-bottom: 32px;
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Headline = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
  font-weight: 700;
  margin: 0 auto;
  text-align: left;
  position: relative;

  strong {
    color: ${COLOR_YELLOW};
  }

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

export const V4: FC<HeadingProps> = (props) => {
  const {
    title = defaultWebValue,
    content = defaultWebValue,
    textColor,
    meta,
    hidden,
    customBackgroundColor,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const { isDashboard } = useSales();

  // Accessibility improvements
  const titleAccessKey = generateAccessKey('het', order);
  const contentAccessKey = generateAccessKey('hec', order);
  const skipLinkId = `skip-to-heading-${meta['accessorKey'] || 'heading'}`;

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Heading edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  return (
    <HeadSubhead
      className={containerClassname}
      aria-label="Left-aligned page heading section with subheading first"
      role="banner"
      style={{
        display:
          (hidden && !websiteAssembler) || (!websiteAssembler && isTitleEmpty && isContentEmpty)
            ? 'none'
            : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: customBackgroundColor || '#fff'
      }}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to main heading</a>
      </span>

      <Wrapper id={skipLinkId}>
        <Subheadline
          id={content.id}
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
          style={{ color: filterBackgroundColor(customBackgroundColor || '#fff', textColor) }}
          isEmpty={isContentEmpty}
          {...(isDashboard
            ? {
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  try {
                    updateActiveElement(e);
                  } catch (error) {
                    handleEditError(error);
                  }
                },
                onBlur: resetActiveElement,
                'data-accessor-parent': meta['accessorKey'],
                'data-accessorkey': 'content',
                'data-placeholder': '+',
                editable: true
              }
            : {
                role: 'textbox',
                'aria-multiline': 'true',
                'aria-label':
                  isContentEmpty || !content.val?.trim()
                    ? 'Page subheading (left-aligned)'
                    : undefined,
                'aria-describedby': title.id,
                accessKey: contentAccessKey,
                title: `Press Alt+${contentAccessKey} to focus this left-aligned subheading`,
                tabIndex: 0
              })}
        />

        <Headline
          id={title.id}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText(title.val, isDashboard)
          }}
          style={{ color: filterBackgroundColor(customBackgroundColor || '#fff', textColor) }}
          isEmpty={isTitleEmpty}
          {...(isDashboard
            ? {
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  try {
                    updateActiveElement(e);
                  } catch (error) {
                    handleEditError(error);
                  }
                },
                onBlur: resetActiveElement,
                'data-accessor-parent': meta['accessorKey'],
                'data-accessorkey': 'title',
                'data-placeholder': '+',
                editable: true
              }
            : {
                'aria-label':
                  isTitleEmpty || !title.val?.trim()
                    ? 'Main page heading (left-aligned)'
                    : undefined,
                'aria-describedby': content.id,
                accessKey: titleAccessKey,
                title: `Press Alt+${titleAccessKey} to focus this left-aligned main heading`,
                tabIndex: 0
              })}
        />
      </Wrapper>
    </HeadSubhead>
  );
};

V4.displayName = 'Heading V4';
